import cx from 'classnames';
import PropTypes from 'prop-types';

import withBreakpoint from 'core/hocs/withBreakpoint';

import {
  MOBILE,
  DESKTOP,
} from 'core/components/breakpoint/values';

import styles from './index.styl';

import breakpointPropTypes from 'core/utils/prop-types/breakpoint';

const breakpointsList = [
  MOBILE,
  DESKTOP,
];

/**
 * Блок создающий ячейку с контентом учитывающий заданный размер грида
 * с возможностью декларативно указывать размеры для разных лейаутов
 */
function GridBlock(props) {
  const {
    width,
    maxWidth,
    order,
    children,
    breakpoint,
    calcWidthByContent,
    className,
  } = props;

  const hasBaseWidth = typeof(width) === 'number';
  const hasBreakpoint = typeof(props[breakpoint]) === 'number';
  const hasCustomWidth = !hasBreakpoint && typeof(width) === 'string';

  const baseWidth = hasCustomWidth ? width : undefined;
  const blockWidth = typeof(props[breakpoint]) === 'string'
    ? props[breakpoint]
    : baseWidth;

  const isVisible = breakpointsList.reduce((result, name) => {
    if (props[name] === 0 && breakpoint === name) return false;
    return result;
  }, !hasBaseWidth || !!width || !!props[breakpoint]);

  const points = breakpointsList.reduce((result, name) => {
    const value = props[name];
    const hasNumericValue = typeof(value) === 'number' && value >= 0;

    if (hasNumericValue && breakpoint === name) {
      const breakpointSelector = styles[`_${name}-${value}`];
      result.push(breakpointSelector);
    }
    return result;
  }, []);

  const reorders = Object
    .keys(props)
    .filter(name => !name.indexOf('reorder'))
    .reduce((result, name) => {
      const breakpointOrder = name.replace('reorder', '').toLowerCase();
      const shift = parseInt(props[name], 10);
      result[breakpointOrder] = shift + (shift > 0 ? 1 : -1);
      return result;
    }, {});

  return (
    <div
      className={cx(
        styles.block,
        blockWidth && styles._fixed,
        calcWidthByContent && styles._calcWidthByContent,
        hasBaseWidth && !blockWidth && styles[`_base-${width}`],
        points,
        className,
      )}
      data-qa='lb-block'
      {...(maxWidth || blockWidth) && {
        style: {
          ...maxWidth && { maxWidth },
          ...blockWidth && { width: blockWidth },
        },
      }}
    >
      <style jsx>{`
        .${styles.block}
          order ${order + (reorders[breakpoint] || 0)}
      `}</style>
      {isVisible && children}
    </div>
  );
}

GridBlock.propTypes = {
  /**
   * Значение текущего лейаута приложения.
   *
   * Задается автоматически HOC [`breakpoints`](#breakpoints).
   */
  breakpoint: breakpointPropTypes(),

  /**
   * По умолчанию если значение не указано, то компонент пытается занять все
   * свободное пространство `Section` или 100% ширины других контейнеров.
   *
   * Если значение указано, как строка, то оно на прямую выставляется
   * компоненту и начинает модифицировать дефолтное поведение.
   *
   * Если значение указано, как число, то оно интерпретируется, как размер
   * блока относительно 12 колоночного грида на **всех** лейаутах.
   *
   * Размеры указанные для специфичных лейаутов (`desktop` или `mobile`)
   * являются более приоритетными.
   */
  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),

  /**
   * Ограничивает ширину блока, для улучшения показателей CLS.
   *
   * На медленном интернет соединении может наблюдаться скачок,
   * описанный в задаче UCMS-8505.
   * Кратко:
   *  Имеем сетку контент + сайдбар.
   *  Контент на мгновение оторбажается на всю ширину страницы,
   *  после чего он сужается и отображается вместе с сайдбаром.
   */
  maxWidth: PropTypes.string,

  /**
   * Размер блока относительно 12 колоночного грида на **десктопном** лейауте
   * или строковое css значение ширины.
   */
  desktop: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  ]),

  /**
   * Размер блока относительно 12 колоночного грида на **телефонном** лейауте
   * или строковое css значение ширины.
   */
  mobile: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  ]),

  /**
   * Реордеринг расположения блока на **десктопном** лейауте относительно
   * текущей позиции в рамках родительской секции
   * ([`<GridSection />`](#gridsection)).
   *
   * Может принимать как положительные так и отрицательные значения.
   *
   * Для изменения порядка элментов используется свойство `flex order`.
   */
  reorderDesktop: PropTypes.number,

  /**
   * Реордеринг расположения блока на **телефонном** лейауте относительно
   * текущей позиции в рамках родительской секции.
   *
   * Может принимать как положительные так и отрицательные значения.
   *
   * Для изменения порядка элментов используется свойство `flex order`.
   */
  reorderMobile: PropTypes.number,

  /**
   * Текущая позиция блока в списке потомков родительской секции
   * ([`<GridSection />`](#gridsection)).
   *
   * Задается автоматически блоком [`<GridSection />`](#gridsection).
   */
  order: PropTypes.number,

  /**
   * Флаг переключающий распределение занимаемого места блоком не по свободному
   * месту в секции, а по контенту блока.
   */
  calcWidthByContent: PropTypes.bool,

  /** @ignore */
  children: PropTypes.node,

  /** @ignore */
  className: PropTypes.string,
};

GridBlock.defaultProps = {
  order: 0,
  calcWidthByContent: false,
};

const GridBlockWithHOCs = withBreakpoint(GridBlock);
GridBlockWithHOCs.displayName = 'GridBlock';

export { GridBlock as StorybookComponent };
export default GridBlockWithHOCs;

